/**
 * The island's voice, synthesized — no assets, one AudioContext.
 *
 * Three continuous beds (wind, rain, fire) whose gains follow world state
 * each frame, plus a short signature when a verb is cast. Everything is
 * quiet by design: this is weather heard through a window, not a mix.
 *
 * Browsers gate audio behind a user gesture, so the context is created on
 * the first pointer press. UI-side only — the simulation never hears it.
 */

export type SoundVerb =
  'strike' | 'rain' | 'strengthen' | 'weaken' | 'omen' | 'grasp' | 'release';

export interface SoundLevels {
  /** 0..1 — proximity-weighted active fire near the camera. */
  fire: number;
  /** 0..1 — rainfall presence (a rain verb's wet minute). */
  rain: number;
  /** 0..1 — overall exposure; high and far hears more wind. */
  wind: number;
}

export interface Soundscape {
  readonly enabled: boolean;
  /** Flip the whole scape; returns the new state. */
  toggle(): boolean;
  update(dt: number, levels: SoundLevels): void;
  /**
   * A verb's signature. `focus` is the scale of attention, 0 (one tree) to 1
   * (the watershed): tight casts speak high, short and close; wide casts drop
   * an octave and take their time, because that is how big things sound.
   */
  cast(verb: SoundVerb, focus?: number): void;
  /**
   * The Become moment (`PLAYABLE.md` P5): something has become possible
   * because of how the god acted. Omen's open fifth, answered — the same two
   * notes and then the octave held, so the phrase that was left open closes.
   */
  become(): void;
}

/** A looping noise buffer, shaped by a filter, opened by its own gain. */
interface Bed { gain: GainNode; target: number }

export function createSound(): Soundscape {
  let ctx: AudioContext | null = null;
  let master: GainNode | null = null;
  let enabled = true;
  const beds: Record<'wind' | 'rain' | 'fire', Bed | null> =
    { wind: null, rain: null, fire: null };
  let windLfoPhase = 0;

  const noiseBuffer = (c: AudioContext, seconds: number) => {
    const buf = c.createBuffer(1, c.sampleRate * seconds, c.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    return buf;
  };

  const mkBed = (c: AudioContext, out: AudioNode,
                 type: BiquadFilterType, freq: number, q: number): Bed => {
    const src = c.createBufferSource();
    src.buffer = noiseBuffer(c, 3);
    src.loop = true;
    const filter = c.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = freq;
    filter.Q.value = q;
    const gain = c.createGain();
    gain.gain.value = 0;
    src.connect(filter).connect(gain).connect(out);
    src.start();
    return { gain, target: 0 };
  };

  const boot = () => {
    if (ctx) return;
    ctx = new AudioContext();
    master = ctx.createGain();
    master.gain.value = enabled ? 0.5 : 0;
    master.connect(ctx.destination);
    beds.wind = mkBed(ctx, master, 'bandpass', 320, 0.45);
    beds.rain = mkBed(ctx, master, 'highpass', 1600, 0.6);
    beds.fire = mkBed(ctx, master, 'lowpass', 480, 0.8);
  };
  /**
   * Audio may only START from a gesture — but it can be SUSPENDED again at any
   * time, and it was armed `{ once: true }`, so the first gesture was the only
   * one that could ever resume it. A browser suspends an `AudioContext` when
   * its tab goes to the background, which for this game is not an edge case:
   * the world runs for hours and a player tabs away constantly. Once that
   * happened the world went permanently silent for the rest of the session,
   * and nothing short of a reload brought it back.
   *
   * So: `boot` still happens exactly once — it self-guards on `ctx`, and a
   * second `AudioContext` would be the real bug — but the resume is now
   * attempted on every gesture and on every return to the tab. `resume()` on a
   * running context is a no-op that resolves immediately, so the cost of the
   * repeat is nothing.
   */
  const wake = () => { boot(); void ctx?.resume(); };
  addEventListener('pointerdown', wake);
  addEventListener('keydown', wake);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) void ctx?.resume();
  });

  const ease = (bed: Bed | null, dt: number) => {
    if (!bed || !ctx) return;
    const k = 1 - Math.pow(0.002, dt);
    bed.gain.gain.value += (bed.target - bed.gain.gain.value) * k;
  };

  /** A short shaped noise sweep — thunder, gust, hush — one-shot. */
  const sweep = (from: number, to: number, dur: number, peak: number,
                 type: BiquadFilterType = 'lowpass') => {
    if (!ctx || !master || !enabled) return;
    const src = ctx.createBufferSource();
    src.buffer = noiseBuffer(ctx, dur + 0.1);
    const filter = ctx.createBiquadFilter();
    filter.type = type;
    filter.frequency.setValueAtTime(from, ctx.currentTime);
    filter.frequency.exponentialRampToValueAtTime(Math.max(40, to), ctx.currentTime + dur);
    const g = ctx.createGain();
    g.gain.setValueAtTime(peak, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(1e-3, ctx.currentTime + dur);
    src.connect(filter).connect(g).connect(master);
    src.start();
    src.stop(ctx.currentTime + dur + 0.1);
  };

  /** A soft sine figure — the melodic half of a verb's signature. */
  const figure = (notes: Array<[number, number, number]>, wave: OscillatorType = 'sine') => {
    if (!ctx || !master || !enabled) return;
    for (const [freq, at, dur] of notes) {
      const osc = ctx.createOscillator();
      osc.type = wave;
      osc.frequency.value = freq;
      const g = ctx.createGain();
      const t0 = ctx.currentTime + at;
      g.gain.setValueAtTime(0, t0);
      g.gain.linearRampToValueAtTime(0.05, t0 + 0.03);
      g.gain.exponentialRampToValueAtTime(1e-3, t0 + dur);
      osc.connect(g).connect(master);
      osc.start(t0);
      osc.stop(t0 + dur + 0.05);
    }
  };

  return {
    get enabled() { return enabled; },
    toggle() {
      enabled = !enabled;
      if (master && ctx) {
        master.gain.setTargetAtTime(enabled ? 0.5 : 0, ctx.currentTime, 0.15);
      }
      return enabled;
    },
    update(dt, levels) {
      if (!ctx) return;
      // wind gusts on its own slow breath over the requested level
      windLfoPhase += dt * 0.35;
      const gust = 0.72 + Math.sin(windLfoPhase) * 0.18 + Math.sin(windLfoPhase * 2.7) * 0.10;
      if (beds.wind) beds.wind.target = 0.05 * levels.wind * gust;
      if (beds.rain) beds.rain.target = 0.16 * levels.rain;
      if (beds.fire) beds.fire.target = 0.22 * levels.fire;
      ease(beds.wind, dt);
      ease(beds.rain, dt);
      ease(beds.fire, dt);
    },
    cast(verb, focus = 0.5) {
      boot();
      if (!enabled) return;
      // one dial, three consequences: pitch falls, time stretches, and the
      // whole thing leans louder as the god's attention opens out
      const f = focus < 0 ? 0 : focus > 1 ? 1 : focus;
      const p = 1.42 + (0.66 - 1.42) * f;      // pitch multiplier
      const d = 0.70 + (1.75 - 0.70) * f;      // duration multiplier
      const g = 0.78 + (1.18 - 0.78) * f;      // gain multiplier
      const fig = (notes: Array<[number, number, number]>, wave?: OscillatorType) =>
        figure(notes.map(([hz, at, dur]) =>
          [hz * p, at * d, dur * d] as [number, number, number]), wave);
      switch (verb) {
        case 'strike':   // the crack, then the roll
          sweep(3200 * p, 90 * p, 1.4 * d, 0.5 * g);
          fig([[52, 0.05, 1.2]], 'triangle');
          break;
        case 'rain':     // a hush arriving
          sweep(900 * p, 2400 * p, 1.2 * d, 0.12 * g, 'highpass');
          fig([[392, 0, 0.5], [329.6, 0.22, 0.7]]);
          break;
        case 'strengthen':    // a small rising light
          fig([[329.6, 0, 0.4], [415.3, 0.12, 0.4], [493.9, 0.24, 0.8]]);
          break;
        case 'weaken':   // the same light, going out
          fig([[311.1, 0, 0.5], [233.1, 0.2, 0.9]], 'triangle');
          sweep(600 * p, 120 * p, 1.0 * d, 0.08 * g);
          break;
        /*
         * GRASP HAD NO VOICE AT ALL, and it is the one verb that is pure
         * contact. Taking borrowed nothing and dropping borrowed
         * *strengthen's* rising figure — so setting a stone down sounded like
         * a blessing, which is the one thing it is not.
         *
         * Neither is a musical figure, and that is the point: the other four
         * verbs are a god's intent and speak in pitched intervals; this is a
         * hand on an object, so it is short, dry and unpitched-sounding. Take
         * lifts a little, release falls and stops dead — no tail, because a
         * thing set on the ground does not ring.
         */
        case 'grasp':
          sweep(180 * p, 320 * p, 0.13 * d, 0.30 * g, 'bandpass');
          fig([[110, 0, 0.09]], 'triangle');
          break;
        case 'release':
          sweep(300 * p, 90 * p, 0.16 * d, 0.34 * g, 'lowpass');
          fig([[78, 0.02, 0.13]], 'triangle');
          break;
        case 'omen':
          // A QUESTION, NOT A STATEMENT. Omen puts nothing into the land, so
          // it must not sound like anything arriving — no impact, no sweep
          // downward, nothing that resolves. Two notes a fifth apart, the
          // second held and unanswered, so the phrase ends open. The player
          // should hear that something was *asked* of the world.
          fig([[440, 0, 0.9], [659.3, 0.30, 1.6]]);
          break;
      }
    },
    become() {
      boot();
      if (!enabled) return;
      figure([[440, 0, 0.5], [659.3, 0.26, 0.5], [880, 0.52, 1.8]]);
    },
  };
}
